import {MapLatLng} from 'raain-ui';
import {
    GaugeNode,
    IComparePerDate,
    ICompares,
    PositionValue,
    QualityIndicatorMethod,
    QualityPoint,
    RainComputationQuality,
    RainNode,
    SpeedMatrix,
    SpeedMatrixContainer,
} from 'raain-model';
import {XYType} from '../xytype';
import {ProfileService} from '../profile.service';
import {FrameSet} from './FrameSet';

export interface ICompareTarget {
    date: Date;
    rainComputationId?: string;
    rainComputationCumulativeId?: string;
}

export interface IUICompare {
    date: Date;
    name: string;
    comparePoints: XYType[];
    comparePointsMax: XYType;
    comparePointsHistory: QualityPoint[];
    remarks: string;
    qualityIndicator: number;
}

export class CompareManager {
    public gaugesInMap: MapLatLng[];
    public gaugesInCompare: MapLatLng[];
    public selectedGauges: MapLatLng[];
    public selectedPixels: MapLatLng[];
    public pixelsSolutions: MapLatLng[][];
    public speeds: MapLatLng[];

    // from comparesTimeline
    public compareNames: string[];
    public uiCompares: IUICompare[];
    public compareSpeedMatrix: PositionValue[];
    public compareVersion: string;
    public compareTrustedIndicator: number;
    public compareDates: Date[];

    public currentQualityDoneDate: Date;
    public currentQualityLaunchedBy: string;
    public currentQualityTimeSpentInMs: number;
    public globalComparePoints: XYType[];
    public globalComparePointsMax: XYType;
    public globalQualityIndicator: number;
    public compareSpeed: {angleInDegrees: number; pixelsPerPeriod: number};
    protected buildCompares: ICompares;
    protected selectedGauge: QualityPoint;
    protected selectedGaugeInPoints: QualityPoint;
    protected selectedGaugeInPointsDetail: string;
    protected rainComputationQualitiesPerDate: any;

    constructor(
        protected profileService: ProfileService,
        public onChanges: () => void = () => {}
    ) {
        this.cleanAll();
    }

    // Merged cumulative from granular comparisons (computed from buildCompares)
    public get granularCumulativePoints(): XYType[] {
        if (!this.buildCompares?.compareCumulative?.qualityPoints?.length) {
            return [];
        }
        return this.buildCompares.compareCumulative.qualityPoints.map(
            (qp) => new XYType(qp.getGaugeValue(), qp.getRainValue(), 2, qp.gaugeLabel, qp.gaugeId)
        );
    }

    public get granularCumulativePointsMax(): XYType {
        if (!this.buildCompares?.compareCumulative) {
            return new XYType(0, 0);
        }
        const maxValue = this.buildCompares.compareCumulative.maxValue * 1.2;
        return new XYType(maxValue, maxValue);
    }

    public get granularCumulativeQualityIndicator(): number {
        if (!this.buildCompares?.compareCumulative?.qualityPoints?.length) {
            return null;
        }
        return SpeedMatrix.ComputeQualityIndicator(
            this.buildCompares.compareCumulative.qualityPoints,
            {method: QualityIndicatorMethod.NASH_SUTCLIFFE}
        );
    }

    protected _rainNode: RainNode;

    public get rainNode(): RainNode {
        return this._rainNode;
    }

    public set rainNode(rainNode: RainNode) {
        this._rainNode = rainNode;
        this.cleanAll();
    }

    public cleanAll() {
        this.gaugesInMap = [];
        this.gaugesInCompare = [];
        this.selectedGauges = [];
        this.selectedPixels = [];
        this.pixelsSolutions = [];
        this.compareDates = [];
        this.speeds = [];

        this.buildCompares = {
            comparesPerDate: [],
            compareCumulative: null,
        };
        this.globalComparePoints = [];
        this.globalComparePointsMax = new XYType(0, 0);
        this.globalQualityIndicator = null;
        // granularCumulative* are now getters computed from buildCompares
        this.rainComputationQualitiesPerDate = {};

        this.selectedGauge = null;
        this.selectedGaugeInPoints = null;
        this.cleanCompareQuality('no compare yet');
    }

    async refreshCompareQuality(frameSet: FrameSet, onlyOne: boolean) {
        const comparePerDate = this.getComparePerDate(frameSet.date);

        if (
            !comparePerDate ||
            !comparePerDate.rainComputationQuality?.qualitySpeedMatrixContainer
        ) {
            // this.cleanCompareQuality(
            //     'no current quality ' + Object.keys(this.rainComputationQualitiesPerDate)
            // );
            // this.onChanges();
            return;
        }

        if (onlyOne) {
            comparePerDate.compareTimeline = comparePerDate.compareTimeline.filter(
                (c) => c.date.getTime() === frameSet.date.getTime()
            );
        }

        const uiCompares: IUICompare[] = comparePerDate.compareTimeline
            .map((c) => {
                const qualityIndicator = SpeedMatrix.ComputeQualityIndicator(c.qualityPoints, {
                    method: QualityIndicatorMethod.NASH_SUTCLIFFE,
                });
                return {
                    date: c.date,
                    name: c.name,
                    comparePoints: c.qualityPoints.map(
                        (p) =>
                            new XYType(
                                p.getGaugeValue(),
                                p.getRainValue(),
                                2,
                                p.gaugeLabel,
                                p.gaugeId
                            )
                    ),
                    comparePointsMax: new XYType(c.maxValue * 1.2, c.maxValue * 1.2),
                    comparePointsHistory: c.qualityPointsLegacy,
                    remarks: c.remarks,
                    qualityIndicator,
                };
            })
            .sort((a, b) => a.date?.getTime() - b.date?.getTime());

        this.currentQualityDoneDate = comparePerDate.rainComputationQuality.isDoneDate;
        this.currentQualityTimeSpentInMs = comparePerDate.rainComputationQuality.timeSpentInMs;
        this.currentQualityLaunchedBy = comparePerDate.rainComputationQuality.launchedBy;
        this.compareVersion = comparePerDate.rainComputationQuality.getVersion();

        this.compareSpeed =
            comparePerDate.rainComputationQuality.qualitySpeedMatrixContainer.getSpeed();
        this.compareNames = uiCompares.map((c) => c.name);
        this.uiCompares = uiCompares;

        this.onChanges();
    }

    async refreshGlobalCompareQuality(
        targetsOrdered: ICompareTarget[],
        period: {begin: Date; end: Date},
        withCompareDuplicate: boolean,
        provider: string = 'Raain',
        timeStepInMinutes: number = 5
    ) {
        try {
            await this.fetchRainComputationQualities(targetsOrdered, provider, timeStepInMinutes);

            await this.buildComparesTimeline(targetsOrdered, period, withCompareDuplicate);

            if (!this.buildCompares.compareCumulative) {
                // throw Error('needs cumulative compare');
                return;
            }

            const countForAverage = targetsOrdered.length;
            const maxValue = this.buildCompares.compareCumulative.maxValue * 1.2;
            const cumulativePoints = this.buildCompares.compareCumulative.qualityPoints.map(
                (qp) =>
                    new XYType(qp.getGaugeValue(), qp.getRainValue(), 2, qp.gaugeLabel, qp.gaugeId)
            );
            const cumulativePointsMax = new XYType(maxValue, maxValue);
            const cumulativeQualityIndicator = SpeedMatrix.ComputeQualityIndicator(
                this.buildCompares.compareCumulative.qualityPoints,
                {method: QualityIndicatorMethod.NASH_SUTCLIFFE}
            );

            // Set for cumulative mode
            this.globalComparePoints = cumulativePoints;
            this.globalComparePointsMax = cumulativePointsMax;
            this.globalQualityIndicator = cumulativeQualityIndicator;
            // granularCumulative* are now getters computed from buildCompares.compareCumulative
        } catch (e) {
            console.warn(e);
        }
    }

    async setGaugesInMap() {
        // Get all gauge IDs linked to this rainNode
        const rainNodeGaugeIds = this.rainNode.getLinks(GaugeNode.TYPE).map((l) => l.getId());

        // Fetch gauges from API (may be filtered/limited)
        let gaugesToFilter = await this.profileService.getGauges(
            this.rainNode?.id,
            this.rainNode.getCenter()
        );
        gaugesToFilter = gaugesToFilter
            .sort((a, b) => {
                return (
                    a.approxDistanceFrom(this.rainNode.getCenter()) -
                    b.approxDistanceFrom(this.rainNode.getCenter())
                );
            })
            .filter((v, index) => index < 200);

        // Build a map of gauges from API response
        const gaugesFromApi = new Map<
            string,
            {id: string; latitude: number; longitude: number; name: string}
        >();
        for (const gauge of gaugesToFilter) {
            gaugesFromApi.set(gauge.id, gauge);
        }

        // Fetch missing gauges individually (those linked but not in API response)
        const missingGaugeIds = rainNodeGaugeIds.filter((id) => !gaugesFromApi.has(id));
        for (const gaugeId of missingGaugeIds) {
            try {
                const gauge = await this.profileService.getGauge(gaugeId);
                if (gauge) {
                    gaugesFromApi.set(gauge.id, gauge);
                }
            } catch (e) {
                console.warn(`Failed to fetch gauge ${gaugeId}:`, e);
            }
        }

        // Filter to only gauges linked to this rainNode
        const visibleGauges = rainNodeGaugeIds
            .map((id) => gaugesFromApi.get(id))
            .filter((g) => !!g);
        console.log('visibleGauges:', visibleGauges.length, '/', rainNodeGaugeIds.length);

        const gaugesLatLng: MapLatLng[] = [];
        for (const gauge of visibleGauges) {
            gaugesLatLng.push(
                new MapLatLng(gauge.latitude, gauge.longitude, undefined, gauge.id, gauge.name)
            );
        }
        this.gaugesInMap = gaugesLatLng;
    }

    async setGaugesInCompare() {
        const qualityPoints: QualityPoint[] = [];
        for (const compare of this.uiCompares) {
            for (const qualityPoint of compare.comparePointsHistory) {
                const alreadyAdded = qualityPoints.filter(
                    (p) => p?.gaugeId === qualityPoint.gaugeId
                );
                if (alreadyAdded.length === 0) {
                    qualityPoints.push(qualityPoint);
                }
            }
        }

        this.gaugesInCompare = qualityPoints.map((p) => {
            return new MapLatLng(
                p.gaugeCartesianValue.lat,
                p.gaugeCartesianValue.lng,
                0,
                p.gaugeId,
                p.gaugeLabel + '>' + p.getGaugeValue(),
                p.getGaugeValue()
            );
        });

        this.resetSelectedGauges();
    }

    async selectGauge(gaugeId: string, compareIndex: number) {
        if (gaugeId) {
            const xys = this.getCurrentQualityPoints(compareIndex).filter(
                (p) => p.gaugeId === gaugeId
            );
            this.selectedGauge = xys.length === 1 ? xys[0] : null;
        } else {
            this.selectedGauge = null;
        }

        if (!this.selectedGauge) {
            this.resetSelectedGauges();
            const gaugesFiltered = this.gaugesInMap.filter((g) => g.id === gaugeId);
            if (gaugesFiltered.length === 1) {
                this.selectedGauges = [gaugesFiltered[0]];
            }
            return;
        }

        const selectedGaugeInPoints = this.getCurrentQualityPoints(compareIndex).filter(
            (p) => p.gaugeId === this.selectedGauge.gaugeId
        );
        let pixelsSolutions: MapLatLng[][] = [];

        if (selectedGaugeInPoints.length === 1) {
            const remarks = JSON.parse(selectedGaugeInPoints[0].remark).sort(
                (a, b) => a.criteriaAttemptedToBeMinimum - b.criteriaAttemptedToBeMinimum
            );
            console.log(remarks);
            pixelsSolutions = remarks.map((remark) =>
                remark.solutionForGauge.pixels.map(
                    (p) =>
                        new MapLatLng(
                            p.y + 0.005,
                            p.x + 0.01,
                            0,
                            '' + !remark.notPossible,
                            '' + !remark.notPossible,
                            1
                        )
                )
            );

            const timeDeltaInMinutes = selectedGaugeInPoints[0].getTimeDeltaInMinutes();
            this.selectedGaugeInPointsDetail =
                '' +
                selectedGaugeInPoints[0].gaugeLabel +
                ' > ' +
                Math.round(selectedGaugeInPoints[0].getGaugeValue() * 100) / 100 +
                ' / ' +
                Math.round(selectedGaugeInPoints[0].getRainValue() * 100) / 100 +
                ' dT=' +
                timeDeltaInMinutes;

            this.selectedGaugeInPoints = selectedGaugeInPoints[0];
        } else {
            this.selectedGaugeInPoints = null;
            this.selectedGauges = [];
            this.selectedPixels = [];
            this.pixelsSolutions = [];
            return;
        }

        this.selectedGauges = [
            new MapLatLng(
                this.selectedGaugeInPoints.gaugeCartesianValue.lat,
                this.selectedGaugeInPoints.gaugeCartesianValue.lng,
                undefined,
                this.selectedGaugeInPoints.gaugeId,
                this.selectedGaugeInPoints.gaugeLabel +
                    '>' +
                    this.selectedGaugeInPoints.gaugeCartesianValue.value,
                this.selectedGaugeInPoints.gaugeCartesianValue.value
            ),
        ];

        this.selectGaugeInCompare();

        const pixels: MapLatLng[] = [];
        for (const rainCartesianValue of this.selectedGaugeInPoints.rainCartesianValues) {
            pixels.push(
                new MapLatLng(rainCartesianValue.lat + 0.005, rainCartesianValue.lng + 0.01)
            );
        }
        this.selectedPixels = pixels;

        this.pixelsSolutions = pixelsSolutions;
    }

    // Returns all granular quality points across all dates in the period (excludes cumulative end date)
    getAllGranularComparePoints(): {
        date: Date;
        gaugeName: string;
        gaugeValue: number;
        pixelValue: number;
    }[] {
        const result: {date: Date; gaugeName: string; gaugeValue: number; pixelValue: number}[] =
            [];

        if (!this.buildCompares?.comparesPerDate?.length || !this.compareDates?.length) {
            return result;
        }

        // Find the last date (cumulative end date) to exclude it
        const lastDateTime = Math.max(...this.compareDates.map((d) => d.getTime()));

        for (const comparePerDate of this.buildCompares.comparesPerDate) {
            // Skip the last date which contains cumulative values
            if (comparePerDate.date.getTime() >= lastDateTime) {
                continue;
            }
            if (!comparePerDate.compareTimeline?.length) {
                continue;
            }
            // Get first timeline entry (current step) for each date
            const currentCompare = comparePerDate.compareTimeline[0];
            if (!currentCompare?.qualityPoints?.length) {
                continue;
            }
            for (const qp of currentCompare.qualityPoints) {
                result.push({
                    date: comparePerDate.date,
                    gaugeName: qp.gaugeLabel,
                    gaugeValue: qp.getGaugeValue(),
                    pixelValue: qp.getRainValue(),
                });
            }
        }

        return result.sort((a, b) => a.date.getTime() - b.date.getTime());
    }

    protected async buildComparesTimeline(
        targetsOrdered: ICompareTarget[],
        period: {begin: Date; end: Date},
        withCompareDuplicate: boolean
    ): Promise<ICompares> {
        const compareDates = targetsOrdered.map((t) => t.date);

        const qualities: RainComputationQuality[] = compareDates
            .filter((d) => !!d)
            .map((d) => this.getRainComputationQuality(d))
            .filter((rcq) => !!rcq);

        this.compareDates = compareDates?.length > 1 ? compareDates : [period.begin, period.end];
        if (this.compareDates[0].getTime() > period.begin.getTime()) {
            this.compareDates.unshift(period.begin);
        }

        this.buildCompares = SpeedMatrixContainer.BuildCompares(qualities, !withCompareDuplicate);
        return this.buildCompares;
    }

    protected getComparePerDate(date: Date): IComparePerDate {
        const existing = this.buildCompares.comparesPerDate.filter(
            (c) => c.date.getTime() === date.getTime()
        );
        if (existing.length === 1) {
            return existing[0];
        }
        return null;
    }

    protected getCurrentQualityPoints(compareIndex: number) {
        if (
            this.uiCompares.length === 0 ||
            compareIndex < 0 ||
            this.compareNames.length < compareIndex
        ) {
            return [];
        }

        return this.uiCompares[compareIndex].comparePointsHistory;
    }

    protected cleanCompareQuality(details?: string) {
        this.compareVersion = details;
        this.compareNames = [];
        this.uiCompares = [];
        this.compareSpeedMatrix = [];
        this.compareTrustedIndicator = undefined;

        this.currentQualityDoneDate = new Date(-1);
        this.currentQualityLaunchedBy = '';
        this.currentQualityTimeSpentInMs = -1;
    }

    protected async fetchRainComputationQualities(
        targetsOrdered: ICompareTarget[],
        provider: string = 'Raain',
        timeStepInMinutes: number = 5
    ) {
        for (const target of targetsOrdered) {
            if (!this.rainComputationQualitiesPerDate[target.date.toISOString()]) {
                try {
                    let rainComputationQuality: RainComputationQuality;

                    if (target.rainComputationCumulativeId) {
                        rainComputationQuality =
                            await this.profileService.getRainCumulativeCumulativesCompareByDate(
                                this.rainNode,
                                target.rainComputationCumulativeId,
                                target.date,
                                provider,
                                timeStepInMinutes
                            );
                    }

                    if (rainComputationQuality) {
                        let isConsistent = true;
                        const matrices =
                            rainComputationQuality.qualitySpeedMatrixContainer.getMatrices();
                        for (const matrix of matrices) {
                            if (!matrix.isConsistent()) {
                                isConsistent = false;
                            }
                        }

                        if (!isConsistent) {
                            console.warn(
                                `One of rainComputationQuality matrices is not consistent for ${target.date.toISOString()}`
                            );
                        }

                        this.rainComputationQualitiesPerDate[target.date.toISOString()] =
                            rainComputationQuality;
                    }
                } catch (e) {
                    console.warn(e);
                }
            }
        }
    }

    protected getRainComputationQuality(date: Date): RainComputationQuality {
        return this.rainComputationQualitiesPerDate[date.toISOString()];
    }

    protected selectGaugeInCompare() {
        const globalComparePoints = JSON.parse(JSON.stringify(this.globalComparePoints));
        for (const p of globalComparePoints) {
            if (p.id === this.selectedGaugeInPoints?.gaugeId) {
                p.r = 10;
            } else {
                p.r = 2;
            }
        }
        this.globalComparePoints = globalComparePoints;

        for (const compare of this.uiCompares) {
            const comparePoints = JSON.parse(JSON.stringify(compare.comparePoints));
            comparePoints.forEach((p) => {
                if (p.id === this.selectedGaugeInPoints?.gaugeId) {
                    p.r = 10;
                } else {
                    p.r = 3;
                }
            });
            compare.comparePoints = comparePoints;
        }
    }

    protected resetSelectedGauges() {
        // console.log('resetSelectedGauges reset');
        this.selectedGauge = null;
        this.selectedGaugeInPoints = null;
        this.selectedGauges = [];
        this.selectedPixels = [];
        this.pixelsSolutions = [];
        this.selectGaugeInCompare();
    }
}
